/* MÂZIA bag behaviour (spec 06), on top of Horizon's cart engine. Loaded as a module by
   snippets/cart-drawer.liquid (every page with the bag) and sections/mz-main-cart.liquid (/cart).
   All numbers come from Liquid re-renders; this file only adds what the design needs on top:
   - phones: a quick add from a product card or the quick-add sheet shows the "Added to bag" toast and
     keeps the bag closed (the flag is read by assets/cart-drawer.js before it auto-opens the bag);
   - Checkout shows "Opening secure checkout…" for 2.2s, without touching the click Razorpay intercepts;
   - "Order on WhatsApp" gets the pincode checked on a product page (localStorage mazia-m-pin);
   - an add made from inside the bag or /cart (add-on Add, or the quick-add sheet opened there) brings the new
     line into view, takes keyboard focus to it and is announced;
   - keyboard focus survives the re-render after − / + / Remove, and the change is announced;
   - a − / + / Remove the store does not answer puts the line back as the cart holds it, with an error;
   - html.mz-bag-open while the bag is open, mz:sheet-open / mz:sheet-close ({ id: 'bag' }) events, the bag
     closes when the menu, search or wishlist sheet opens, and MZ.emit('bag-open') / MZ.emit('bag-close')
     open or close it from other areas. */
import { StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const TOAST_MS = 2600;
const BUSY_MS = 2200;
const PIN_KEY = 'mazia-m-pin';
const SECTION_ID = 'cart-drawer-section';
const root = document.documentElement;
const phone = window.matchMedia('(max-width: 749px)');

const drawer = () => /** @type {HTMLElement & { open?: () => void, close?: () => void } | null} */ (document.getElementById('cart-drawer'));
const bagIsOpen = () => Boolean(drawer()?.hasAttribute('open'));
const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`mz:${name}`, { detail }));

/* ---------- Toast ---------- */
/* Shows for TOAST_MS, and waits while a finger or the pointer is on it or keyboard focus is in it (WCAG 2.2.1);
   leaving it starts the count again. Only its message is the status region (snippets/mz-cart-toast.liquid), so
   "View bag" is not read out with it. Keyboard focus never falls to <body> because of it: if the toast hides with
   focus inside, and when the bag opened from its "View bag" closes (the bag hides the toast, so Horizon's focus
   return to that button does nothing), focus goes back to the control that added the piece: the card '+', or the
   '+' the quick-add sheet handed focus back to. Failing that, it goes to the header's Bag. */
let toastTimer = 0;
let toastPointer = false;
/** @type {HTMLElement | null} what had focus when the toast showed */
let toastReturn = null;
/** @type {WeakSet<Element>} */
const toastsBound = new WeakSet();

function toastEl() {
  return /** @type {HTMLElement | null} */ (document.querySelector('[data-mz-bag-toast]'));
}

/** @param {unknown} el @returns {el is HTMLElement} on the page, drawn and not hidden, so it can take focus */
const shown = (el) =>
  el instanceof HTMLElement && el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility === 'visible';

/** @returns {HTMLElement | null} the control that added the piece, else the header's Bag (phones or desktop) */
const toastFocusTarget = () =>
  shown(toastReturn)
    ? toastReturn
    : /** @type {HTMLElement | undefined} */ ([...document.querySelectorAll('.mz-header__bag, .mz-header__action--bag')].find(shown)) ||
      null;

const toastShowing = () => Boolean(toastEl()?.classList.contains('is-visible'));

/** Starts the count, unless the toast is held (finger, pointer or keyboard focus on it). */
function countDownToast() {
  window.clearTimeout(toastTimer);
  if (!toastShowing() || toastPointer || toastEl()?.contains(document.activeElement)) return;
  toastTimer = window.setTimeout(() => hideToast(), TOAST_MS);
}

/** @param {HTMLElement} toast */
function bindToast(toast) {
  if (toastsBound.has(toast)) return;
  toastsBound.add(toast);
  const hold = () => window.clearTimeout(toastTimer);
  toast.addEventListener('pointerenter', () => {
    toastPointer = true;
    hold();
  });
  toast.addEventListener('pointerleave', () => {
    toastPointer = false;
    countDownToast();
  });
  toast.addEventListener('focusin', hold);
  toast.addEventListener('focusout', (event) => {
    if (!toast.contains(/** @type {Node | null} */ (event.relatedTarget))) countDownToast();
  });
}

/** Notes what has focus as the toast shows: the control that added the piece (the card '+'). */
function noteToastReturn() {
  const active = document.activeElement;
  toastReturn = null;
  // The quick-add sheet is still closing: once closed, it hands focus back to the '+' that opened it.
  const sheet = active instanceof Element ? active.closest('#quick-add-dialog dialog[open]') : null;
  if (sheet) {
    const later = () =>
      window.setTimeout(() => {
        if (toastShowing()) noteToastReturn();
      }, 0);
    sheet.addEventListener('close', later, { once: true });
    return;
  }
  if (active instanceof HTMLElement && active !== document.body && !active.closest('[data-mz-bag-toast], #cart-drawer')) {
    toastReturn = active;
  }
}

/**
 * @param {{ bagOpening?: boolean }} [options] - bagOpening: the bag is opening and hides the toast; the control
 *   that added the piece is kept, for focus when the bag closes
 */
function hideToast({ bagOpening = false } = {}) {
  window.clearTimeout(toastTimer);
  const toast = toastEl();
  if (!toast || !toast.classList.contains('is-visible')) return;
  // Hidden with keyboard focus inside: focus would fall to <body>
  if (toast.contains(document.activeElement)) toastFocusTarget()?.focus({ preventScroll: true });
  if (!bagOpening) toastReturn = null;
  toast.classList.remove('is-visible');
  root.classList.remove('mz-toast-open');
  emit('toast', { open: false });
}

/** @param {string} text */
function showToast(text) {
  const toast = toastEl();
  if (!toast || bagIsOpen()) return;
  bindToast(toast);
  noteToastReturn();
  const name = toast.querySelector('[data-mz-bag-toast-name]');
  if (name) name.textContent = text;
  toastPointer = false;
  toast.classList.add('is-visible');
  root.classList.add('mz-toast-open');
  emit('toast', { open: true, text });
  countDownToast();
}

/**
 * The added line's label ("Eryon Table lamp · Drum linen shade"), from the bag the add response rendered.
 * @param {any} detail - CartLinesUpdateEvent promise detail
 * @param {string} variantId
 * @param {Element | null} source - element that dispatched the add
 */
function addedLabel(detail, variantId, source) {
  const html = detail?.sections?.[SECTION_ID];
  if (html && variantId) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const row = doc.querySelector(`.mz-bag-line[data-variant-id="${CSS.escape(variantId)}"]`);
      if (row instanceof HTMLElement && row.dataset.mzLabel) return row.dataset.mzLabel;
    } catch (error) {
      /* fall through */
    }
  }
  const live = variantId && document.querySelector(`#cart-drawer .mz-bag-line[data-variant-id="${CSS.escape(variantId)}"]`);
  if (live instanceof HTMLElement && live.dataset.mzLabel) return live.dataset.mzLabel;
  const item = detail?.items?.find?.((/** @type {any} */ line) => String(line.variant_id) === variantId);
  if (item?.product_title) return item.product_title;
  return source?.closest('[data-product-title]')?.getAttribute('data-product-title') || '';
}

/* Runs before Horizon's own listeners (window, capture phase): on phones, an add from a card or the
   quick-add sheet while the bag is closed shows the toast; assets/cart-drawer.js then skips its auto-open.
   An add made from inside the open bag or /cart (an add-on's Add, or the quick-add sheet opened from there)
   brings the new line into view once the lines have re-rendered (spec 06 §2.10: the new line on top) and is
   announced ("Added to bag: Haven Home sign"). The re-render takes the add-on's card out of the rail (or gives
   its Add button to the next add-on), and the quick-add sheet hands focus back to that button when it closes,
   so keyboard focus fell to <body> or onto another add-on's Add, under the pinned buttons: the new line's name
   takes it (unless the shopper has moved focus on meanwhile), and the next Tab continues in that line. A tap,
   or a VoiceOver double tap on iOS, leaves focus where it was (Safari does not focus buttons), and the sheet
   handing focus back to the rail is not the shopper's doing either: both count as not moved on, so the
   VoiceOver cursor follows to the new line too. */
window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const cartEvent = /** @type {any} */ (event);
    if (cartEvent.action !== 'add') return;
    const source = event.target instanceof Element ? event.target : null;
    const merchandiseId = String(cartEvent.lines?.[0]?.merchandiseId || '');
    const variantId = merchandiseId.split('/').pop() || '';
    const fromQuickAdd = Boolean(source?.closest('quick-add-dialog, #quick-add-dialog'));
    const fromBag = bagIsOpen() && (fromQuickAdd || Boolean(source?.closest('#cart-drawer')));
    const fromCartPage =
      !bagIsOpen() && Boolean(document.querySelector('.mz-cart-page')) && (fromQuickAdd || Boolean(source?.closest('.mz-cart-page-wrap')));
    if (fromBag || fromCartPage) {
      // Focus as the add starts: the Add button (keyboard, a screen reader that moves focus, a click in Chrome),
      // the quick-add sheet, or what still had it after a tap.
      const focusAtPress = document.activeElement;
      // The quick-add sheet puts focus (and, on /cart, the page scroll) back when it closes: reveal after that.
      const sheet = fromQuickAdd ? source?.closest('dialog') : null;
      cartEvent.promise
        ?.then((/** @type {any} */ result) => {
          if (result?.detail?.didError) return;
          const label = addedLabel(result?.detail, variantId, source);
          const reveal = () =>
            window.setTimeout(() => revealAddedLine(variantId, focusAtPress, label, 0, fromQuickAdd), 0);
          if (sheet?.open) sheet.addEventListener('close', reveal, { once: true });
          else reveal();
        })
        .catch(() => {});
      return;
    }
    if (!phone.matches || bagIsOpen()) return;
    if (!source || source.closest('#cart-drawer')) return;
    const fromCard = source.closest('.mz-card, product-card, quick-add-component, quick-add-dialog, #quick-add-dialog');
    if (!fromCard) return;

    cartEvent.mzBagToast = true;

    cartEvent.promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        // Let Horizon close the quick-add sheet and re-render the bag first.
        window.setTimeout(() => showToast(addedLabel(result?.detail, variantId, source)), 0);
      })
      .catch(() => {});
  },
  true
);

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function scrollBagToTop() {
  const scroller = [...document.querySelectorAll('#cart-drawer .mz-bag__wrap, #cart-drawer .mz-bag__body')].find(
    (el) => el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'hidden'
  );
  scroller?.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
}

/**
 * Shows the line just added: in the bag it is the top row (Shopify lists the newest line first), so the bag
 * scrolls back to the top; on /cart the page scrolls to that line. Its name takes focus when focus was lost (the
 * re-render removed what had it), is still where it was as the add started, or is where the closing quick-add
 * sheet put it back (an Add in the add-ons rail), and "Added to bag: <line>" is announced.
 * @param {string} variantId
 * @param {Element | null} [focusAtPress] - what had focus as the add started
 * @param {string} [label] - the line's label from the add response, if the line is not found
 * @param {number} [tries] - checks made so far for the new line
 * @param {boolean} [fromSheet] - the add came from the quick-add sheet, which hands focus back to the rail
 */
function revealAddedLine(variantId, focusAtPress = null, label = '', tries = 0, fromSheet = false) {
  const inBag = bagIsOpen();
  const scope = inBag ? drawer() : document.querySelector('.mz-cart-page-wrap');
  if (!scope) return;
  const lines = [...scope.querySelectorAll('.mz-bag-line')];
  const found = lines.find((row) => row.getAttribute('data-variant-id') === variantId);
  // The lines re-render from the add's answer first; if that answer had no bag section, Horizon fetches it
  // afterwards: wait for the new line (up to 2s) rather than focus and name the line that was on top.
  if (!found && variantId && tries < 20) {
    window.setTimeout(() => revealAddedLine(variantId, focusAtPress, label, tries + 1, fromSheet), 100);
    return;
  }
  const line = found || lines[0];
  if (inBag) scrollBagToTop();
  else line?.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });

  const name = line?.querySelector('.mz-bag-line__name');
  const stayed = Boolean(focusAtPress) && document.activeElement === focusAtPress;
  // The sheet gave focus back to the Add it opened from, a node the re-render may have handed to the next add-on
  // (bag rail or the /cart rail, .mz-bag-addons--page): the shopper did not put it there.
  const handedBack = fromSheet && Boolean(document.activeElement?.closest('.mz-bag-addons'));
  if (name instanceof HTMLElement && (focusLost() || stayed || handedBack)) name.focus({ preventScroll: true });

  const region = (inBag ? scope : document).querySelector('[data-mz-bag-status]');
  const added = region?.getAttribute('data-mz-added') || '';
  const what = (line instanceof HTMLElement && line.dataset.mzLabel) || label;
  announce(region, added && what ? `${added}: ${what}` : added || what);
}

/**
 * Says the text from a bag status region (role="status"). Cleared first, so the same words twice in a row are
 * announced twice.
 * @param {Element | null | undefined} region
 * @param {string} text
 */
function announce(region, text) {
  if (!region || !text) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = text;
  }, 80);
}

/* ---------- Bag open / close hooks ---------- */
/* Every open starts at the top of the bag, as the design builds the sheet fresh each time: it never reopens where
   it was closed, and an open that follows an add made outside the bag (Add to bag on a product page, a desktop
   card '+') shows the new line, which Shopify puts on top (at scrollTop 0 the re-render does not shift the view).
   The event fires inside Horizon's open(), right after showModal(), so the reset lands before the first paint;
   the class check skips the second open event Horizon fires for a bag that is already open. */
document.addEventListener('theme-drawer:open', (event) => {
  if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
  if (!root.classList.contains('mz-bag-open')) {
    event.target.querySelectorAll('.mz-bag__wrap, .mz-bag__body').forEach((el) => {
      el.scrollTop = 0;
    });
  }
  hideToast({ bagOpening: true });
  root.classList.add('mz-bag-open');
  emit('sheet-open', { id: 'bag' });
});

document.addEventListener('theme-drawer:close', (event) => {
  if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
  root.classList.remove('mz-bag-open');
  emit('sheet-close', { id: 'bag' });
});

/* Horizon gives focus back to what opened the bag once it has closed. When that was the toast's "View bag" (the
   bag hides the toast) or nothing (a tap in Safari), focus fell to <body>: a keyboard user started again at the
   top of the page and the VoiceOver cursor left the content. The bag dialog's own close event fires a task after
   Horizon's focus return: unless focus is now on something visible outside the bag, it goes to the control that
   added the piece shown in the toast, else the header's Bag. 'close' does not bubble, hence the capture. */
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || dialog.parentElement !== drawer()) return;
    // Closed only to be shown again (Horizon re-stacks it, or switches modal mode, with close() + showModal()),
    // or closed by something else, e.g. Razorpay's checkout (assets/cart-drawer.js then closes the drawer)
    if (dialog.open || bagIsOpen()) return;
    const target = toastFocusTarget();
    toastReturn = null;
    const active = document.activeElement;
    if (active && active !== document.body && !active.closest('#cart-drawer') && shown(active)) return;
    target?.focus({ preventScroll: true });
  },
  true
);

/* One sheet at a time (spec 06 §2.12): another sheet closes the bag; the quick-add sheet stacks over it. */
document.addEventListener('mz:sheet-open', (event) => {
  const id = /** @type {CustomEvent} */ (event).detail?.id;
  if (['menu', 'search', 'wishlist'].includes(id) && bagIsOpen()) drawer()?.close?.();
});

document.addEventListener('mz:bag-open', () => {
  const bag = drawer();
  if (bag?.open) bag.open();
  else window.location.href = (typeof Theme === 'object' && Theme?.routes?.cart_url) || '/cart';
});

document.addEventListener('mz:bag-close', () => drawer()?.close?.());

/* ---------- Checkout: busy label (spec 06 §2.9) ---------- */
/* Window capture runs before Razorpay's document listener; the label changes on the next task, so the
   click and its target are untouched when Razorpay (or the native form submit) handles them. */
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.mz-bag-actions__checkout') : null;
    if (!(button instanceof HTMLElement)) return;
    const label = button.querySelector('.mz-bag-actions__label');
    const busy = button.dataset.mzBusyLabel;
    if (!label || !busy) return;

    window.setTimeout(() => {
      if (!button.dataset.mzIdleLabel) button.dataset.mzIdleLabel = label.textContent || '';
      label.textContent = busy;
      button.setAttribute('aria-busy', 'true');
      window.setTimeout(() => restoreCheckout(button), BUSY_MS);
    }, 0);
  },
  true
);

/** @param {HTMLElement} button */
function restoreCheckout(button) {
  const label = button.querySelector('.mz-bag-actions__label');
  if (label && button.dataset.mzIdleLabel && label.textContent === button.dataset.mzBusyLabel) {
    label.textContent = button.dataset.mzIdleLabel;
  }
  delete button.dataset.mzIdleLabel;
  button.removeAttribute('aria-busy');
}

/* Back from Razorpay or Shopify checkout (bfcache): no busy label left behind. */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('.mz-bag-actions__checkout[aria-busy="true"]').forEach((button) => {
    if (button instanceof HTMLElement) restoreCheckout(button);
  });
  hideToast();
});

/* ---------- Order on WhatsApp: add the checked pincode (spec 06 §2.9) ---------- */
/* data-mz-wa-pin holds the URL-encoded line, e.g. "Pincode%3A%20%5Bpincode%5D". The server href is kept in
   data-mz-wa-base until the next bag re-render, which replaces both. */
document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-mz-wa-order]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    let pin = '';
    try {
      pin = String(window.localStorage.getItem(PIN_KEY) || '').trim();
    } catch (error) {
      pin = '';
    }
    const base = link.dataset.mzWaBase || link.getAttribute('href') || '';
    link.dataset.mzWaBase = base;
    const template = link.dataset.mzWaPin || 'Pincode%3A%20%5Bpincode%5D';
    link.setAttribute('href', /^[1-9]\d{5}$/.test(pin) ? `${base}%0A${template.replace('%5Bpincode%5D', pin)}` : base);
  },
  true
);

/* ---------- Keyboard focus and announcements across bag updates (spec 06 §2.3) ---------- */
/* − / + / Remove re-render the lines, and the re-render (a line's key changes with its bundle discount, so
   its row is rebuilt) or the row's removal takes the focused control out of the page: focus fell to <body>.
   The control is noted before Horizon handles the click (window capture). While that update is in flight,
   focus that falls to <body> goes back to the same − / + / Remove of the same line (matched by line key, then
   variant), or, once the line is gone, to the next (else the previous) line's name, or to the bag title when
   the bag is empty. Once the update has settled, the line's "Quantity, <name>: n" is announced from the
   status region next to the lines ([data-mz-bag-status], never re-rendered); after a removal in the bag, the
   piece count, which the header's live region cannot announce behind the modal bag. */
const BAG_CONTROL = '.mz-bag-step__btn, .mz-bag-line__remove';
const UPDATE_MS = 15000;

/**
 * @typedef {{ key: string, variant: string }} LineRef
 * @typedef {{ scope: Element, line: LineRef, control: string, near: LineRef[], restore: boolean,
 *   failed: boolean, observer: MutationObserver | null, timer: number }} BagIntent
 */

/** @type {BagIntent | null} */
let bagIntent = null;

/** @param {Element} row @returns {LineRef} */
const lineRef = (row) => ({ key: row.getAttribute('data-key') || '', variant: row.getAttribute('data-variant-id') || '' });

const focusLost = () => {
  const active = document.activeElement;
  return !active || active === document.body || !active.isConnected;
};

function endBagIntent() {
  if (!bagIntent) return;
  bagIntent.observer?.disconnect();
  window.clearTimeout(bagIntent.timer);
  bagIntent = null;
}

/** @param {BagIntent} intent @returns {Element[]} the lines on screen (a removed one fading out does not count) */
const bagRows = (intent) =>
  [...intent.scope.querySelectorAll('.mz-bag-line')].filter((row) => !row.classList.contains('removing'));

/**
 * @param {Element[]} rows
 * @param {LineRef} ref
 * @param {boolean} [byVariant] - also match by variant (a quantity change gives the line a new key)
 */
const findRow = (rows, ref, byVariant = true) =>
  rows.find((row) => ref.key && row.getAttribute('data-key') === ref.key) ||
  (byVariant ? rows.find((row) => ref.variant && row.getAttribute('data-variant-id') === ref.variant) : undefined);

/** The intent's own line, if it is still in the bag. A line being removed is only found by its key. */
const intentRow = (/** @type {BagIntent} */ intent) => findRow(bagRows(intent), intent.line, intent.control !== 'remove');

/**
 * @param {BagIntent} intent
 * @returns {HTMLElement | null}
 */
function bagFocusTarget(intent) {
  /** @param {Element | null | undefined} el @returns {el is HTMLElement} */
  const usable = (el) => el instanceof HTMLElement && !el.matches(':disabled') && el.getClientRects().length > 0;
  const row = intentRow(intent);
  if (row) {
    const own =
      intent.control === 'remove'
        ? row.querySelector('.mz-bag-line__remove')
        : row.querySelector(`.mz-bag-step__btn[name="${intent.control}"]`);
    return [own, ...row.querySelectorAll('.mz-bag-step__btn, .mz-bag-line__remove, .mz-bag-line__name')].find(usable) || null;
  }
  const rows = bagRows(intent);
  const near = intent.near.map((ref) => findRow(rows, ref)).find(Boolean) || rows[0];
  if (near) return /** @type {HTMLElement | null} */ (near.querySelector('.mz-bag-line__name'));
  return /** @type {HTMLElement | null} */ (intent.scope.querySelector('#cart-drawer-heading, .mz-cart-page__title'));
}

/** @param {BagIntent} intent */
function restoreBagFocus(intent) {
  if (!intent.scope.isConnected || (intent.scope.id === 'cart-drawer' && !bagIsOpen())) return;
  const target = bagFocusTarget(intent);
  if (!target) return;
  target.focus({ preventScroll: true });
  // Keep it on screen: within the bag's scrolling part, above its pinned buttons (the scroller's
  // scroll-padding-bottom in assets/mz-cart.css), or the page on /cart.
  const box = target.getBoundingClientRect();
  let top = 0;
  let bottom = window.innerHeight;
  for (let el = target.parentElement; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      const frame = el.getBoundingClientRect();
      top = Math.max(top, frame.top);
      bottom = Math.min(bottom, frame.bottom - (parseFloat(style.scrollPaddingBottom) || 0));
      break;
    }
  }
  if (box.top < top || box.bottom > bottom) target.scrollIntoView({ block: 'center' });
}

/**
 * @param {BagIntent} intent
 * @param {number} count - pieces in the bag after the update
 */
function announceBagChange(intent, count) {
  const inBag = intent.scope.id === 'cart-drawer';
  const region = (inBag ? intent.scope : document).querySelector('[data-mz-bag-status]');
  if (!region) return;
  let text = '';
  const row = intent.control === 'remove' ? null : intentRow(intent);
  if (row) {
    text = (row.querySelector('[data-mz-qty-status]')?.textContent || '').replace(/\s+/g, ' ').trim();
  } else if (inBag && Number.isFinite(count)) {
    // "Total pieces in bag: 2", the header's own words (Theme is the global from snippets/scripts.liquid)
    const label = typeof Theme === 'object' ? Theme?.translations?.cart_count : '';
    text = label ? `${label}: ${count}` : String(count);
  }
  announce(region, text);
}

window.addEventListener(
  'click',
  (event) => {
    const control = event.target instanceof Element ? event.target.closest(BAG_CONTROL) : null;
    const row = control?.closest('.mz-bag-line');
    const scope = control?.closest('#cart-drawer, .mz-cart-page-wrap');
    if (!(control instanceof HTMLElement) || !row || !scope) return;
    endBagIntent();
    const rows = [...scope.querySelectorAll('.mz-bag-line')];
    const index = rows.indexOf(row);
    /** @type {BagIntent} */
    const intent = {
      scope,
      line: lineRef(row),
      control: control.classList.contains('mz-bag-line__remove') ? 'remove' : control.getAttribute('name') || 'plus',
      near: [...rows.slice(index + 1), ...rows.slice(0, index).reverse()].map(lineRef),
      // A tap on a touch screen does not focus the button: then there is no place to keep, only the announcement.
      restore: document.activeElement === control,
      failed: false,
      observer: null,
      timer: window.setTimeout(endBagIntent, UPDATE_MS),
    };
    if (intent.restore) {
      // Also moves focus off a row that has started its removal animation, so a second Enter cannot reach
      // its controls (Horizon removes lines by position: it would take the next line too).
      intent.observer = new MutationObserver(() => {
        if (focusLost() || document.activeElement?.closest('.mz-bag-line.removing')) restoreBagFocus(intent);
      });
      intent.observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    bagIntent = intent;
  },
  true
);

/** Calls back once Horizon has finished this update: its re-render drops .cart-items-disabled. */
function whenCartSettles(/** @type {Element} */ items, /** @type {() => void} */ callback) {
  if (!items.classList.contains('cart-items-disabled')) {
    callback();
    return;
  }
  const observer = new MutationObserver(() => {
    if (items.classList.contains('cart-items-disabled')) return;
    observer.disconnect();
    window.clearTimeout(timer);
    callback();
  });
  const timer = window.setTimeout(() => observer.disconnect(), UPDATE_MS);
  observer.observe(items, { attributes: true, attributeFilter: ['class'] });
}

window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const cartEvent = /** @type {any} */ (event);
    const intent = bagIntent;
    if (!intent || (cartEvent.action !== 'update' && cartEvent.action !== 'remove')) return;
    const items = event.target instanceof Element ? event.target.closest('cart-items-component') : null;
    if (!items || !intent.scope.contains(items)) return;
    let count = NaN;
    cartEvent.promise
      ?.then((/** @type {any} */ result) => {
        count = Number(result?.cart?.totalQuantity ?? result?.detail?.itemCount);
      })
      .catch(() => {});
    whenCartSettles(items, () =>
      requestAnimationFrame(() => {
        if (bagIntent !== intent) return;
        if (intent.restore && (focusLost() || document.activeElement?.closest('.mz-bag-line.removing'))) {
          restoreBagFocus(intent);
        }
        // A refused or unanswered change says so in the line's error row (role="alert") instead.
        if (!intent.failed) announceBagChange(intent, count);
        endBagIntent();
      })
    );
  },
  true
);

/* ---------- A − / + / Remove that gets no answer (spec 06 §2.3, §2.12 "Error") ---------- */
/* When /cart/change gets no JSON answer (offline, a 5xx, a 429 page), Horizon's cart engine fires
   shopify:cart:error with code SERVICE_UNAVAILABLE and leaves the lines as the tap drew them: the new number
   between − and +, the line price and Total dimmed for good, a removed line gone, although the cart has not
   changed (and the next + would send that number plus one). The bag goes back to what the cart holds: each
   changed number to its last rendered value (the input's value attribute), the prices undimmed, a removed line
   back in its place (the section as it was when the removal started), and the line's error row says
   "Couldn't update your bag…" (data-mz-update-error on the lines' form, Theme settings → MÂZIA bag). Shopify's
   own refusals (code INVALID, e.g. a quantity limit) keep Horizon's handling: the number goes back and the row
   shows Shopify's message. */

/** @typedef {{ pending: number, before: string }} BagChange */
/** @type {WeakMap<Element, BagChange>} a bag or /cart cart-items-component → its − / + / Remove in flight */
const bagChanges = new WeakMap();

/** The bag's or the /cart page's cart-items-component, when it is the event's target. */
const bagItems = (/** @type {EventTarget | null} */ target) =>
  target instanceof Element && target.matches('cart-items-component') && target.closest('#cart-drawer, .mz-cart-page-wrap')
    ? target
    : null;

const pause = (/** @type {number} */ ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

/** @param {Element} items @returns {string} the section around these lines, as it is now */
function sectionMarkup(items) {
  const id = items.getAttribute('data-section-id');
  return (id && document.getElementById(`shopify-section-${id}`)?.outerHTML) || '';
}

/** Dispatched by Horizon's updateQuantity before its request: a removal takes the row out at once. */
window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const cartEvent = /** @type {any} */ (event);
    if (cartEvent.action !== 'update' && cartEvent.action !== 'remove') return;
    const items = bagItems(event.target);
    if (!items) return;
    const change = bagChanges.get(items) || { pending: 0, before: '' };
    change.pending += 1;
    if (cartEvent.action === 'remove') change.before = sectionMarkup(items);
    bagChanges.set(items, change);
    // Settles a microtask after a failure's shopify:cart:error, so that handler still sees it pending.
    const settled = () => {
      change.pending = Math.max(0, change.pending - 1);
      if (!change.pending) change.before = '';
    };
    cartEvent.promise?.then(settled, settled);
  },
  true
);

window.addEventListener(
  StandardEvents.cartError,
  (event) => {
    const cartEvent = /** @type {any} */ (event);
    const items = bagItems(event.target);
    if (!items) return;
    const intent = bagIntent && bagIntent.scope.contains(items) ? bagIntent : null;
    if (intent) intent.failed = true;
    const change = bagChanges.get(items);
    // Only a change of these lines (a later section refresh can fail too, after the cart did change)
    if (cartEvent.code !== 'SERVICE_UNAVAILABLE' || !change?.pending) return;
    undoBagChange(items, change.before, intent);
  },
  true
);

/**
 * @param {Element} items
 * @param {string} before - the section when a removal started ('' for a quantity change)
 * @param {BagIntent | null} intent - the − / + / Remove pressed, if it was pressed in this bag
 */
function undoBagChange(items, before, intent) {
  items.querySelectorAll('[shimmer]').forEach((el) => el.removeAttribute('shimmer'));
  for (const input of items.querySelectorAll('.mz-bag-step__input')) {
    const row = input.closest('.mz-bag-line');
    if (!(input instanceof HTMLInputElement) || input.value === input.defaultValue || !row) continue;
    if (row.classList.contains('removing')) continue;
    input.value = input.defaultValue;
    /** @type {any} */ (input.closest('cart-quantity-selector-component'))?.updateButtonStates?.();
    showLineError(row);
  }
  if (before) putBackRemovedLines(items, before, intent);
}

/**
 * Horizon folds a removed row away (200ms) and takes it out a task later, or swaps in the empty bag a frame after
 * the press: once that is done, the section goes back to how it was, and the line that came back says why.
 * @param {Element} items
 * @param {string} before
 * @param {BagIntent | null} intent
 */
async function putBackRemovedLines(items, before, intent) {
  for (let i = 0; i < 40 && items.querySelector('.mz-bag-line.removing'); i += 1) await pause(50);
  await pause(0);
  await nextFrame();
  await nextFrame();
  const id = items.getAttribute('data-section-id');
  // Another change has started meanwhile: its answer re-renders the bag from the cart.
  if (!id || !items.isConnected || bagChanges.get(items)?.pending) return;

  const doc = new DOMParser().parseFromString(before, 'text/html');
  doc.querySelectorAll('.cart-items-disabled').forEach((el) => el.classList.remove('cart-items-disabled'));
  doc.querySelectorAll('[shimmer]').forEach((el) => el.removeAttribute('shimmer'));
  const onScreen = new Set([...items.querySelectorAll('.mz-bag-line')].map((row) => row.getAttribute('data-key')));
  const gone = [...doc.querySelectorAll('.mz-bag-line')]
    .map((row) => row.getAttribute('data-key') || '')
    .filter((key) => key && !onScreen.has(key));
  if (!gone.length) return;

  try {
    await morphSection(id, doc.documentElement.outerHTML, { mode: items.hasAttribute('data-drawer') ? 'hydration' : 'full' });
  } catch (error) {
    return;
  }
  const scope = document.querySelector(`cart-items-component[data-section-id="${CSS.escape(id)}"]`);
  if (!scope) return;
  scope.querySelectorAll('cart-quantity-selector-component').forEach((selector) => {
    /** @type {any} */ (selector).updateButtonStates?.();
  });
  for (const key of gone) {
    const row = [...scope.querySelectorAll('.mz-bag-line')].find((line) => line.getAttribute('data-key') === key);
    if (row) showLineError(row);
  }
  // Keyboard focus moved on when the line left: back to the Remove (or −) that was pressed.
  if (intent?.restore && (focusLost() || intent.scope.contains(document.activeElement))) restoreBagFocus(intent);
}

/**
 * Shows "Couldn't update your bag…" (the lines' data-mz-update-error) in the line's error row.
 * @param {Element} row - a bag line
 */
function showLineError(row) {
  const message = row.closest('[data-mz-update-error]')?.getAttribute('data-mz-update-error') || '';
  const box = row.querySelector('.mz-bag-line__error');
  const text = row.querySelector('.mz-bag-line__error-text');
  if (!box || !text || !message) return;
  box.classList.remove('hidden');
  // role="alert": cleared first, so a second failure in a row is announced again
  text.textContent = '';
  window.setTimeout(() => {
    text.textContent = message;
  }, 80);
}

/* For other areas: the bag area runs the toast (do not build a second one). */
if (window.MZ) window.MZ.bagToast = true;
